import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';
import { LogLevel } from '../../../types/dex';

interface TokenApprovalProps {
  tokenAddress: string;
}

/**
 * 代币授权组件
 * 允许用户进行ERC20代币授权操作
 */
const TokenApproval: React.FC<TokenApprovalProps> = ({ tokenAddress }) => {
  const { dexService, config, isInitialized, addLog } = useDexService();
  const [selectedWallet, setSelectedWallet] = useState('');
  const [selectedWalletIndex, setSelectedWalletIndex] = useState<number>(0);
  const [spenderAddress, setSpenderAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [approvalAmount, setApprovalAmount] = useState<string | null>(null);
  const [tokenSymbol, setTokenSymbol] = useState<string>('');
  const [tokenDecimals, setTokenDecimals] = useState<number>(18);

  // 当tokenAddress变化时获取代币信息
  useEffect(() => {
    if (tokenAddress) {
      checkTokenInfo();
    }
  }, [tokenAddress]);

  // 处理钱包选择变更
  const handleWalletChange = (event: any) => {
    const walletAddress = event.target.value;
    if (!walletAddress) {
      addLog({
        level: LogLevel.WARNING,
        message: '选择的钱包地址无效'
      });
      return;
    }
    
    // 找到对应的钱包索引
    const index = dexService?.getWalletAddresses().findIndex(
      addr => addr.toLowerCase() === walletAddress.toLowerCase()
    ) ?? -1;
    
    if (index === -1) {
      addLog({
        level: LogLevel.ERROR,
        message: '找不到选择的钱包'
      });
      return;
    }

    setSelectedWallet(walletAddress);
    setSelectedWalletIndex(index);
    setApprovalAmount(null);
    setError(null);
    setSuccess(null);
    
    addLog({
      level: LogLevel.INFO,
      message: `已切换到钱包: ${walletAddress} (索引: ${index})`
    });
  };

  // 处理被授权地址变更
  const handleSpenderAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSpenderAddress(e.target.value);
    setApprovalAmount(null);
    setError(null);
    setSuccess(null);
  };

  // 处理授权金额变更
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    setError(null);
    setSuccess(null);
  };

  // 自动填充DEX路由器地址
  const handleAutofillRouterAddress = () => {
    if (config?.dex?.router?.address) {
      setSpenderAddress(config.dex.router.address);
      addLog({
        level: LogLevel.INFO,
        message: `已自动填充DEX路由器地址: ${config.dex.router.name}`
      });
    } else {
      addLog({
        level: LogLevel.WARNING,
        message: '未找到DEX路由器地址，请手动输入'
      });
    }
  };

  // 检查代币信息
  const checkTokenInfo = async () => {
    if (!isInitialized || !dexService) {
      setError('DexService未初始化，请先配置服务');
      addLog({
        level: LogLevel.ERROR,
        message: 'DexService未初始化，无法获取代币信息'
      });
      return;
    }

    if (!tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
      setError('请输入有效的代币合约地址');
      addLog({
        level: LogLevel.WARNING,
        message: '代币地址无效，请检查输入'
      });
      return;
    }

    setCheckLoading(true);
    setError(null);

    try {
      const tokenInfo = await dexService.getTokenInfo(tokenAddress);
      setTokenSymbol(tokenInfo.symbol);
      setTokenDecimals(tokenInfo.decimals);
      addLog({
        level: LogLevel.SUCCESS,
        message: `成功获取代币信息: ${tokenInfo.name} (${tokenInfo.symbol}), 小数位: ${tokenInfo.decimals}`
      });
    } catch (err: any) {
      console.error('获取代币信息时出错:', err);
      setError(`获取代币信息时出错: ${err.message || '未知错误'}`);
      addLog({
        level: LogLevel.ERROR,
        message: `获取代币信息失败: ${err.message || '未知错误'}`
      });
    } finally {
      setCheckLoading(false);
    }
  };

  // 检查当前授权金额
  const checkApproval = async () => {
    if (!isInitialized || !dexService) {
      setError('DexService未初始化，请先配置服务');
      addLog({
        level: LogLevel.ERROR,
        message: 'DexService未初始化，无法检查授权金额'
      });
      return;
    }

    if (!tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
      setError('请输入有效的代币合约地址');
      addLog({
        level: LogLevel.WARNING,
        message: '代币地址无效，无法检查授权金额'
      });
      return;
    }

    if (!selectedWallet) {
      setError('请选择一个钱包');
      addLog({
        level: LogLevel.WARNING,
        message: '未选择钱包，无法检查授权金额'
      });
      return;
    }

    if (!spenderAddress || !ethers.utils.isAddress(spenderAddress)) {
      setError('请输入有效的被授权地址');
      addLog({
        level: LogLevel.WARNING,
        message: '被授权地址无效，无法检查授权金额'
      });
      return;
    }

    setCheckLoading(true);
    setError(null);
    setSuccess(null);
    setApprovalAmount(null);

    try {
      const allowance = await dexService.getAllowance(tokenAddress, selectedWallet, spenderAddress);
      
      // 如果尚未获取代币信息，先获取
      if (!tokenSymbol) {
        const tokenInfo = await dexService.getTokenInfo(tokenAddress);
        setTokenSymbol(tokenInfo.symbol);
        setTokenDecimals(tokenInfo.decimals);
      }
      
      const formattedAllowance = ethers.utils.formatUnits(allowance, tokenDecimals);
      setApprovalAmount(formattedAllowance);
      
      addLog({
        level: LogLevel.SUCCESS,
        message: `当前授权金额: ${formattedAllowance} ${tokenSymbol}`
      });
    } catch (err: any) {
      console.error('检查授权金额时出错:', err);
      setError(`检查授权金额时出错: ${err.message || '未知错误'}`);
      addLog({
        level: LogLevel.ERROR,
        message: `检查授权金额失败: ${err.message || '未知错误'}`
      });
    } finally {
      setCheckLoading(false);
    }
  };

  // 执行授权
  const handleApprove = async () => {
    if (!isInitialized || !dexService || !config || !selectedWallet || !tokenAddress || !spenderAddress) {
      setError('请确保所有必要信息已填写');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const amountToApprove = amount === 'max' ? 
        ethers.constants.MaxUint256 : 
        ethers.utils.parseUnits(amount, tokenDecimals);

      // 使用钱包索引而不是地址
      const walletAddress = dexService.getWalletAddresses()[selectedWalletIndex];
      if (!walletAddress) {
        throw new Error('找不到选择的钱包');
      }

      const tx = await dexService.approve(
        tokenAddress,
        spenderAddress,
        amountToApprove,
        walletAddress
      );

      addLog({
        level: LogLevel.INFO,
        message: '代币授权交易已发送',
        data: { txHash: tx.hash }
      });

      await tx.wait();
      
      addLog({
        level: LogLevel.SUCCESS,
        message: '代币授权成功',
        data: { txHash: tx.hash }
      });

      setSuccess(`授权成功! 交易哈希: ${tx.hash}`);
      
      // 清空输入
      setAmount('');
      setSpenderAddress('');
      setTokenSymbol('');
      setTokenDecimals(18);
    } catch (err: any) {
      console.error('授权代币时出错:', err);
      setError(err.message || '授权失败');
      addLog({
        level: LogLevel.ERROR,
        message: `授权代币失败: ${err.message}`,
        data: { error: err }
      });
    } finally {
      setLoading(false);
    }
  };

  // 复制交易哈希
  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash).then(
      () => {
        addLog({
          level: LogLevel.INFO,
          message: '交易哈希已复制到剪贴板'
        });
      },
      (err) => {
        addLog({
          level: LogLevel.ERROR,
          message: '复制交易哈希失败'
        });
      }
    );
  };

  // 设置最大授权
  const handleSetMaxApproval = () => {
    setAmount('max');
  };

  if (!isInitialized || !config) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Alert severity="warning">
          DexService未初始化，请先配置服务
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        代币授权测试
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 钱包选择 */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>选择钱包</InputLabel>
            <Select
              value={selectedWallet}
              label="选择钱包"
              onChange={handleWalletChange}
            >
              {config.wallets.map((wallet, index) => (
                <MenuItem 
                  key={wallet.address || `wallet-${index}`} 
                  value={wallet.address || ''}
                >
                  钱包 {index + 1}: {(wallet.address || '').slice(0, 6)}...{(wallet.address || '').slice(-4)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 被授权地址 */}
        <Grid item xs={12} md={6}>
          <TextField
            label="被授权地址 (Spender)"
            variant="outlined"
            fullWidth
            value={spenderAddress}
            onChange={handleSpenderAddressChange}
            placeholder="输入被授权的合约或钱包地址"
            size="small"
            InputProps={{
              endAdornment: (
                <Tooltip title="使用DEX路由器地址">
                  <Button 
                    size="small" 
                    onClick={handleAutofillRouterAddress}
                    disabled={!config?.dex?.router?.address}
                  >
                    自动填充
                  </Button>
                </Tooltip>
              )
            }}
          />
          {spenderAddress && config?.dex?.router?.address === spenderAddress && (
            <Typography variant="caption" sx={{ ml: 1 }}>
              DEX路由器: {config?.dex?.router?.name}
            </Typography>
          )}
        </Grid>

        {/* 授权金额 */}
        <Grid item xs={12} md={6}>
          <TextField
            label="授权金额"
            variant="outlined"
            fullWidth
            value={amount}
            onChange={handleAmountChange}
            placeholder="输入授权金额或设置为最大值"
            size="small"
            InputProps={{
              endAdornment: (
                <Tooltip title="设置为最大授权金额 (无限制)">
                  <Button 
                    size="small" 
                    onClick={handleSetMaxApproval}
                  >
                    最大值
                  </Button>
                </Tooltip>
              )
            }}
          />
        </Grid>
      </Grid>

      {/* 操作按钮 */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button 
          variant="outlined" 
          color="primary"
          onClick={checkApproval}
          disabled={checkLoading || !selectedWallet || !spenderAddress}
          startIcon={checkLoading ? <CircularProgress size={20} /> : null}
        >
          检查授权
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleApprove}
          disabled={loading || !selectedWallet || !spenderAddress || !amount}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          授权代币
        </Button>
      </Box>

      {/* 提示信息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          action={
            <IconButton
              aria-label="copy"
              color="inherit"
              size="small"
              onClick={() => handleCopyHash(success.split(': ')[1])}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {/* 当前授权金额 */}
      {approvalAmount !== null && (
        <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="body2">
              当前授权金额: <strong>{approvalAmount}</strong> {tokenSymbol}
              {approvalAmount === '0.0' && ' (未授权)'}
              {approvalAmount === '115792089237316195423570985008687907853269984665640564039457.584007913129639935' && ' (最大授权)'}
            </Typography>
          </Box>
        </Card>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        注意：在进行代币交换前，必须先授权DEX路由器合约使用您的代币。
      </Alert>
    </Paper>
  );
};

export default TokenApproval;
